import React, { useState, useEffect } from "react";

export default function App() {
  const [studyDetails, setStudyDetails] = useState("");
  const [abnormalFindings, setAbnormalFindings] = useState("");
  const [report, setReport] = useState("");
  const [suggestions, setSuggestions] = useState([]); // ✅ New state for suggestions
  const [listening, setListening] = useState(false);

  // ✅ Your backend URL
  const backendBaseUrl = "https://radiology-report-api-64048f911dce.herokuapp.com";

  // ✅ Handle generating the report (same as before, but adds suggestions)
  const handleGenerateReport = async () => {
    if (!studyDetails || !abnormalFindings) {
      alert("Please provide both study details and abnormal findings.");
      return;
    }

    try {
      const response = await fetch(`${backendBaseUrl}/generate-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ studyDetails, abnormalFindings }),
      });

      const data = await response.json();

      if (data.report) {
        setReport(data.report);
        setSuggestions(data.suggestions || []); // ✅ Add suggestions if returned
      } else {
        alert("Failed to generate report.");
      }
    } catch (error) {
      console.error("Report generation error:", error);
      alert("Error generating report. Check backend connection.");
    }
  };

  // ✅ Start speech recognition with auto punctuation
  const startListening = (field) => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.autoPunctuation = true;

    recognition.start();
    setListening(true);

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript;
      transcript = cleanTranscript(transcript);

      if (field === "study") {
        setStudyDetails((prev) => (prev + " " + transcript).trim());
      } else if (field === "findings") {
        setAbnormalFindings((prev) => (prev + " " + transcript).trim());
      }

      setListening(false);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };
  };

  // ✅ Clean punctuation replacement
  const cleanTranscript = (text) => {
    return text
      .replace(/\bperiod\b/gi, ".")
      .replace(/\bcomma\b/gi, ",")
      .replace(/\bquestion mark\b/gi, "?")
      .replace(/\bexclamation point\b/gi, "!")
      .replace(/ +/g, " ")
      .replace(/\.([a-zA-Z])/g, ". $1")
      .trim();
  };

  // ✅ Handle copying the report
  const handleCopyReport = () => {
    navigator.clipboard
      .writeText(report)
      .then(() => alert("Report copied!"))
      .catch((err) => console.error("Copy failed:", err));
  };

  // ✅ Handle clicking a suggestion to add it to abnormal findings
  const handleSuggestionClick = (suggestion) => {
    setAbnormalFindings((prev) => {
      if (prev.includes(suggestion)) {
        return prev;
      }
      return `${prev}${prev ? ". " : ""}${suggestion}.`;
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">
        Radiology Report Generator
      </h1>

      <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto w-full">
        {/* Study Details Section */}
        <div>
          <label className="block mb-2 font-semibold">Study Details</label>
          <textarea
            className="w-full p-3 rounded bg-gray-800 border border-gray-700 text-white"
            rows="3"
            value={studyDetails}
            onChange={(e) => setStudyDetails(e.target.value)}
            placeholder="e.g., CT Chest with contrast for lung nodule evaluation"
          ></textarea>
          <button
            className={`mt-2 px-4 py-2 rounded ${
              listening ? "bg-gray-600" : "bg-blue-600 hover:bg-blue-700"
            } text-white`}
            onClick={() => startListening("study")}
            disabled={listening}
          >
            {listening ? "Listening..." : "Dictate Study Details"}
          </button>
        </div>

        {/* Abnormal Findings Section */}
        <div>
          <label className="block mb-2 font-semibold">Abnormal Findings</label>
          <textarea
            className="w-full p-3 rounded bg-gray-800 border border-gray-700 text-white"
            rows="5"
            value={abnormalFindings}
            onChange={(e) => setAbnormalFindings(e.target.value)}
            placeholder="Dictate or type abnormal findings..."
          ></textarea>
          <button
            className={`mt-2 px-4 py-2 rounded ${
              listening ? "bg-gray-600" : "bg-purple-600 hover:bg-purple-700"
            } text-white`}
            onClick={() => startListening("findings")}
            disabled={listening}
          >
            {listening ? "Listening..." : "Dictate Findings"}
          </button>
        </div>

        {/* Suggestions Panel */}
        {suggestions.length > 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded p-4">
            <h2 className="text-lg font-semibold mb-2 text-yellow-400">
              Suggestions to consider:
            </h2>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Generate Report Button */}
        <button
          className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
          onClick={handleGenerateReport}
        >
          Generate Report
        </button>

        {/* Generated Report Section */}
        <div>
          <label className="block mb-2 font-semibold">Generated Report</label>
          <textarea
            className="w-full p-3 rounded bg-gray-800 border border-gray-700 text-white"
            rows="15"
            value={report}
            onChange={(e) => setReport(e.target.value)}
            placeholder="Generated report will appear here..."
          ></textarea>
        </div>

        {/* Copy Report Button */}
        <button
          className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white"
          onClick={handleCopyReport}
        >
          Copy Report
        </button>
      </div>
    </div>
  );
}
